var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo } from 'react';
import { t } from 'i18next';
import { nanoid } from 'nanoid';
import { NotificationType } from '@sovryn/ui';
import { EXPORT_RECORD_LIMIT } from '../../../../../../constants/general';
import { getTokenDisplayName } from '../../../../../../constants/tokens';
import { useNotificationContext } from '../../../../../../contexts/NotificationContext';
import { translations } from '../../../../../../locales/i18n';
import { rskClient } from '../../../../../../utils/clients';
import { useGetCloseWithSwapsLazyQuery, useGetCloseWithSwapsQuery, } from '../../../../../../utils/graphql/rsk/generated';
import { dateFormat } from '../../../../../../utils/helpers';
import { useGetLoanIds } from '../../../hooks/useGetLoanIds';
export const useGetCloseWithSwapLoans = (pageSize, page, orderOptions) => {
    const { loanIds, loading: loadingLoanIds, refetch: refetchLoanIds, } = useGetLoanIds();
    const { addNotification } = useNotificationContext();
    const config = useMemo(() => ({
        loanIds,
        skip: page * pageSize,
        pageSize,
        orderBy: orderOptions.orderBy || undefined,
        orderDirection: orderOptions.orderDirection || undefined,
    }), [
        loanIds,
        page,
        pageSize,
        orderOptions.orderBy,
        orderOptions.orderDirection,
    ]);
    const [getCloseWithSwaps] = useGetCloseWithSwapsLazyQuery({
        client: rskClient,
    });
    const { loading, data, refetch: refetchLoans, } = useGetCloseWithSwapsQuery({
        variables: config,
        client: rskClient,
    });
    const list = useMemo(() => {
        if (!data) {
            return [];
        }
        return data.closeWithSwaps.map(tx => ({
            id: tx.transaction.id,
            loanId: tx.loanId.id,
            loanCloseAmount: tx.loanCloseAmount,
            positionCloseSize: tx.positionCloseSize,
            collateralToken: tx.loanId.collateralToken.symbol,
            loanToken: tx.loanId.loanToken.symbol,
            timestamp: tx.timestamp,
            hash: tx.transaction.id,
        }));
    }, [data]);
    const refetch = useCallback(() => {
        refetchLoans();
        refetchLoanIds();
    }, [refetchLoanIds, refetchLoans]);
    const exportData = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const { data } = yield getCloseWithSwaps({
            variables: Object.assign(Object.assign({}, config), { skip: 0, pageSize: EXPORT_RECORD_LIMIT }),
        });
        let list = (data === null || data === void 0 ? void 0 : data.closeWithSwaps) || [];
        if (!list || !list.length) {
            addNotification({
                type: NotificationType.warning,
                title: t(translations.common.tables.actions.noDataToExport),
                content: '',
                dismissible: true,
                id: nanoid(),
            });
        }
        return list.map(tx => {
            return {
                timestamp: dateFormat(tx.timestamp),
                transactionType: t(translations.borrowHistory.transactionTypes.closeSwap),
                collateralWithdrawn: `${tx.positionCloseSize} ${getTokenDisplayName(tx.loanId.collateralToken.symbol || '')}`,
                debtRepaid: `${tx.loanCloseAmount} ${getTokenDisplayName(tx.loanId.loanToken.symbol || '')}`,
                loanId: tx.loanId.id,
                TXID: tx.transaction.id,
            };
        });
    }), [addNotification, config, getCloseWithSwaps]);
    return {
        loading: loading || loadingLoanIds,
        data: list,
        refetch,
        exportData,
    };
};
